"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Scale, Calculator } from "lucide-react";
import { useTranslations } from "next-intl";
import { useFormatPrice } from "@/components/price/useFormatPrice";
import { calculateBookingPrice, cn, pluralRu } from "@/lib/utils";
import { compareQueryFromIds, readCompareCarIds, toggleCompareCarId } from "@/lib/compare-cars";
import Button from "@/components/ui/Button";

const PRESET_DAYS = [3, 7, 14] as const;

const PRIMARY_LINK =
  "inline-flex items-center justify-center rounded-xl bg-[#E63946] px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-[#c1121f] focus-visible:outline focus-visible:ring-2 focus-visible:ring-red-400";

export default function CarPageRentalAndCompare({
  carId,
  bookingPath,
  pricePerDay,
  deposit,
  outOfCityAllowed,
  outOfCitySurcharge,
}: {
  carId: string;
  bookingPath: string;
  pricePerDay: number;
  deposit: number | null;
  outOfCityAllowed: boolean;
  outOfCitySurcharge: number | null;
}) {
  const t = useTranslations("CarDetailPage");
  const formatMoney = useFormatPrice();
  const [trip, setTrip] = useState<"within_city" | "out_of_city">("within_city");
  const [days, setDays] = useState<number>(7);
  const [inCompare, setInCompare] = useState(false);
  const [compareList, setCompareList] = useState<string[]>([]);

  useEffect(() => {
    const sync = () => {
      const n = readCompareCarIds();
      setCompareList(n);
      setInCompare(n.includes(carId));
    };
    const t = window.setTimeout(sync, 0);
    window.addEventListener("beepbeep-compare-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("beepbeep-compare-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [carId]);

  const activeTrip: "within_city" | "out_of_city" = !outOfCityAllowed ? "within_city" : trip;

  const sur =
    activeTrip === "out_of_city" && outOfCitySurcharge != null && Number(outOfCitySurcharge) > 0
      ? Number(outOfCitySurcharge)
      : 0;

  const { total, fee, rentalSubtotal, outOfCitySubtotal } = calculateBookingPrice(pricePerDay, days, 0, sur);

  function onCompareClick() {
    const { action, ids } = toggleCompareCarId(carId);
    setCompareList(ids);
    setInCompare(ids.includes(carId));
    if (action === "limit") {
      window.alert(t("compareLimitMsg"));
    }
  }

  const compareHref = `/cars/compare?ids=${compareQueryFromIds(compareList)}`;

  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900/60"
      aria-labelledby="rental-estimate-heading"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
          <Calculator size={18} className="text-[#E63946]" aria-hidden />
          <h2 id="rental-estimate-heading" className="text-base font-bold">
            {t("estimateTitle")}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={inCompare ? "secondary" : "outline"}
            className="gap-1.5"
            onClick={onCompareClick}
          >
            <Scale size={14} aria-hidden />
            {inCompare ? t("compareRemove") : t("compareAdd")}
          </Button>
          {compareList.length > 0 && (
            <Link
              href={compareHref}
              className={cn(
                "inline-flex items-center justify-center rounded-xl border px-3 py-1.5 text-sm font-medium",
                "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800",
              )}
            >
              {t("compareOpen")}
            </Link>
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t("estimateHint")}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {PRESET_DAYS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              days === d
                ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946]"
                : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300"
            }`}
          >
            {d} {pluralRu(d, "день", "дня", "дней")}
          </button>
        ))}
      </div>

      {outOfCityAllowed && (
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name={`est-trip-${carId}`}
              checked={trip === "within_city"}
              onChange={() => setTrip("within_city")}
              className="text-[#E63946]"
            />
            {t("estimateWithinCity")}
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name={`est-trip-${carId}`}
              checked={trip === "out_of_city"}
              onChange={() => setTrip("out_of_city")}
              className="text-[#E63946]"
            />
            {t("estimateOutCity")}
          </label>
        </div>
      )}

      <dl className="mt-4 space-y-2 rounded-xl bg-gray-50 px-4 py-3 text-sm dark:bg-gray-800/50">
        <div className="flex justify-between text-gray-600 dark:text-gray-300">
          <dt>{t("estimateRental")}</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">
            {formatMoney(rentalSubtotal)} · {days} {pluralRu(days, "день", "дня", "дней")}
          </dd>
        </div>
        {outOfCitySubtotal > 0 && (
          <div className="flex justify-between text-gray-600 dark:text-gray-300">
            <dt>{t("estimateOutCityFee")}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(outOfCitySubtotal)}</dd>
          </div>
        )}
        {deposit != null && deposit > 0 && (
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <dt>{t("estimateDeposit")}</dt>
            <dd>{formatMoney(deposit)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-bold text-gray-900 dark:border-gray-700 dark:text-gray-100">
          <dt>{t("estimateTotal")}</dt>
          <dd>{formatMoney(total)}</dd>
        </div>
        {fee > 0 && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {t("estimateFeeNote", { fee: formatMoney(fee) })}
          </p>
        )}
      </dl>

      <div className="mt-4">
        <Link href={bookingPath} className={PRIMARY_LINK}>
          {t("estimateBookCta")}
        </Link>
      </div>
    </section>
  );
}
